// Hand-written query documents. queries.ts is overwritten by `amplify codegen`
// and its by-index queries select scalars only, so anything needing a nested
// field lives here. The operation name MUST stay identical to the generated
// one where a document replaces it — utils.ts queryList and the pagination
// below derive the response key from the operation name, not the root field.
import { API, graphqlOperation } from "aws-amplify";
import { GraphQLResult } from "@aws-amplify/api";
import * as APITypes from "../API";

// Same shape as utils.ts filterDeleted (module-private there, and importing
// utils would drag its whole UI import chain into this leaf module).
const filterDeleted = {
  not: {
    deletedAt: {
      contains: "",
    },
  },
};

type GeneratedTransactionItem = NonNullable<
  NonNullable<
    APITypes.TransactionsByBookkeepingYearIdQuery["transactionsByBookkeepingYearId"]
  >["items"][number]
>;

// The generated query type carries no `bill` — this is the honest shape of
// what transactionsByBookkeepingYearIdWithBill actually selects.
export type TransactionWithBillItem = GeneratedTransactionItem & {
  bill?: { id: string; status: APITypes.BillStatus } | null;
};

type TransactionsByBookkeepingYearIdWithBillQuery = {
  transactionsByBookkeepingYearId?: {
    items: Array<TransactionWithBillItem | null>;
    nextToken?: string | null;
  } | null;
};

// transactionsByBookkeepingYearId (queries.ts) + `bill { id status }`: the
// row-edit gate mirrors the app's `bill?.status != paid` check and needs the
// bill status hydrated with the list, not per-row.
export const transactionsByBookkeepingYearIdWithBill = /* GraphQL */ `query TransactionsByBookkeepingYearId(
  $bookkeepingYearId: ID!
  $sortDirection: ModelSortDirection
  $filter: ModelTransactionFilterInput
  $limit: Int
  $nextToken: String
) {
  transactionsByBookkeepingYearId(
    bookkeepingYearId: $bookkeepingYearId
    sortDirection: $sortDirection
    filter: $filter
    limit: $limit
    nextToken: $nextToken
  ) {
    items {
      id
      deletedAt
      paidAt
      currencyCode
      amount
      title
      exchangeRate
      baseCurrencyAmount
      status
      exportedAt
      accountingSystemId
      bookkeepingYearId
      billId
      bill {
        id
        status
      }
      bookingAccountId
      transactionReferenceId
      userId
      replacedById
      authGroups
      authGroupsRead
      createdAt
      updatedAt
      __typename
    }
    nextToken
    __typename
  }
}
`;

// Paginated fetch that survives nested-field auth redaction. Bill carries no
// head-read auth rule (schema.graphql), unlike Transaction: a head reading
// another user's transactions gets each nested bill nulled, which Amplify
// surfaces as a REJECTION that still carries the page data. The rows
// themselves are readable — recover them; a redacted bill stays null and the
// edit gate falls open to editable, the same outcome the app has under the
// same auth. Deleted rows are excluded like utils.ts queryList does
// (includeDeleted = false path).
export const listTransactionsWithBill = async (
  variables: APITypes.TransactionsByBookkeepingYearIdQueryVariables
): Promise<TransactionWithBillItem[]> => {
  const filter = variables.filter
    ? { and: [variables.filter, filterDeleted] }
    : filterDeleted;
  const items: TransactionWithBillItem[] = [];
  let nextToken: string | null | undefined;
  do {
    const response: GraphQLResult<TransactionsByBookkeepingYearIdWithBillQuery> =
      await (API.graphql(
        graphqlOperation(transactionsByBookkeepingYearIdWithBill, {
          ...variables,
          filter,
          nextToken,
        })
      ) as Promise<
        GraphQLResult<TransactionsByBookkeepingYearIdWithBillQuery>
      >).catch((error) => {
        if (error?.data?.transactionsByBookkeepingYearId?.items) {
          return error;
        }
        throw error;
      });
    const page = response.data?.transactionsByBookkeepingYearId;
    items.push(
      ...(page?.items ?? []).filter(
        (item): item is TransactionWithBillItem => item !== null
      )
    );
    nextToken = page?.nextToken;
  } while (nextToken);
  return items;
};
